import { mergeQueryString, updateSearch } from 'bernie-core';
import { KeyedObject } from 'bernie-core/dist/source';
import { AnalyticsStore } from 'bernie-plugin-mobx';
import { History, Location } from 'history';
import { parse } from 'query-string';
import { ActivityDestinationViewSearch } from '../common/__generated__/api';
import { ActivityDetailBasic } from '../common/__generated__/api/types';
import { getFilteredObjectKeys } from '../helpers/common-helper';
import { buildMicroMessage } from '../mappers/analytics-store';
import { ActivitySearchStore, FILTER_URL_PARAM_PREFIX, SearchCriteriaStore } from '../stores';

const REGION_HOTEL = 'hotel';

export function trackSrpTileClick(
  activity: ActivityDestinationViewSearch.ActivityInlineFragment,
  index: number,
  analytics: AnalyticsStore
): void {
  const allActivityProducts = buildMicroMessage('allActivityProducts', {
    numOfProducts: 1,
    activityProducts: [{ productId: parseInt(activity.id, 10), sortPosition: index + 1 }],
  });
  if (activity.clientSideAnalytics) {
    analytics.trackEvent(
      activity.clientSideAnalytics.referrerId,
      activity.clientSideAnalytics.linkName,
      analytics.omnitureData,
      null,
      true,
      [allActivityProducts]
    );
  }
}

export function updateSearchQueryString(
  searchCriteriaStore: SearchCriteriaStore,
  history: History,
  location: Location
): void {
  if (searchCriteriaStore && !searchCriteriaStore.isQueryStringUpdated) {
    searchCriteriaStore.isQueryStringUpdated = true;
    const searchQuery = searchCriteriaStore.searchQuery;
    const queryParams = parse(location.search);
    if (searchQuery && searchCriteriaStore.isQueryKeysDifferent(queryParams)) {
      updateSearch({
        history: history,
        location: location,
        newParams: searchQuery,
        newState: {},
        replace: true,
      });
    }
  }
}

function getRegionDetails(activitySearchStore: ActivitySearchStore): ActivityDetailBasic.Region {
  const regionType = (activitySearchStore.query && activitySearchStore.query.regionType === REGION_HOTEL) || null;
  const resolvedRegion = activitySearchStore.searchSummary && activitySearchStore.searchSummary.resolvedRegion;
  const queryLocation = activitySearchStore.query ? activitySearchStore.query.location : '';

  return {
    regionName: (regionType && queryLocation) || (resolvedRegion && resolvedRegion.fullRegionName) || queryLocation,
    regionId: (regionType && activitySearchStore.query.rid) || (resolvedRegion && resolvedRegion.regionId) || '',
    __typename: 'ActivityRegion',
  };
}

export function updateSearchCriteriaStore(
  searchCriteriaStore: SearchCriteriaStore,
  activitySearchStore: ActivitySearchStore,
  query: KeyedObject
): void {
  const searchSummary = activitySearchStore.searchSummary;
  const searchedDates = searchSummary && searchSummary.dateValidationResult;

  const swpApplied = query.swp !== 'off';

  const isoStartDate = (searchedDates && searchedDates.formattedStartDate) || '';
  const isoEndDate = (searchedDates && searchedDates.formattedEndDate) || '';

  const { regionName, regionId } = getRegionDetails(activitySearchStore);
  const filterKeys = getFilteredObjectKeys(query, FILTER_URL_PARAM_PREFIX);
  const filterParams: KeyedObject = {};
  filterKeys.forEach((filterKey) => {
    filterParams[filterKey] = query[filterKey];
  });

  searchCriteriaStore.searchQuery = {
    ...filterParams,
    location: regionName || '',
    sort: query.sort,
    slimSearchKeyword: query.slimSearchKeyword,
    swp: swpApplied ? 'on' : 'off',
    startDate: isoStartDate,
    endDate: isoEndDate,
    rid: regionId,
    selectedId: query.selectedId,
    mctc: query.mctc,
    travelDurationFromHotelEnabled: query.travelDurationFromHotelEnabled,
    regionType: query.regionType,
    latLong: query.latLong,
    landingPage: query.landingPage,
  };
}

export function handlePillsClick(
  history: History,
  location: Location,
  selections: ActivityDestinationViewSearch.Selections[],
  pageId: string,
  analytics: AnalyticsStore
): void {
  if (selections.length > 0) {
    const queryParams = parse(location.search);
    const filterCategory = selections[0].id;
    if (filterCategory === 'slimSearchKeyword') {
      const slimSearchKeyword: string = (queryParams && queryParams.slimSearchKeyword) || '';

      if (slimSearchKeyword === selections[0].value) {
        analytics.trackEvent(
          `LX.SR.Remove.Keyword.${selections[0].value}`,
          'keyword-pill-removed',
          analytics.omnitureData
        );
        updateSearch({
          history: history,
          location: location,
          newParams: {
            slimSearchKeyword: '',
          },
        });
      }
    } else {
      const filterCategoryKey = FILTER_URL_PARAM_PREFIX + filterCategory;
      const oldFiltersString = (queryParams && queryParams[filterCategoryKey]) || '';

      const filters = oldFiltersString.split(',').filter((filter: string) => filter !== '');
      selections.forEach((selection) => {
        const indexOfFilterToDelete = filters.indexOf(selection.value);
        if (indexOfFilterToDelete > -1) {
          filters.splice(indexOfFilterToDelete, 1);
        }
      });
      const filtersString = filters.join(',');

      if (filterCategory === 'seeAll') {
        analytics.trackEvent('LX.SR.Remove.SeeAll', 'see-all-pill-removed', analytics.omnitureData);
        if (queryParams && queryParams.regionType) {
          updateSearch({
            history: history,
            location: location,
            newParams: {
              [filterCategoryKey]: filtersString,
              sort: 'RECOMMENDED',
              selectedId: '',
              regionType: '',
            },
          });
        } else {
          updateSearch({
            history: history,
            location: location,
            newParams: {
              [filterCategoryKey]: filtersString,
              sort: 'RECOMMENDED',
              selectedId: '',
            },
          });
        }
      } else {
        analytics.trackEvent(
          `LX.${pageId}.Remove.${filterCategory}.${selections[0].value}`,
          `${filterCategory}-pill-removed`,
          analytics.omnitureData
        );
        updateSearch({
          history: history,
          location: location,
          newParams: {
            [filterCategoryKey]: filtersString,
          },
        });
      }
    }
  }
}

export function getBreadcrumbUrl(
  history: History,
  location: Location,
  selectionsToRemove: ActivityDestinationViewSearch.SelectionsToRemove[]
): string {
  let searchQueryString = history.location.search;
  const queryParams = parse(location.search);
  selectionsToRemove.forEach((selectionToRemove: ActivityDestinationViewSearch.SelectionsToRemove) => {
    const key = selectionToRemove.id;
    const existingUrlFilterValues = queryParams && queryParams[key] ? queryParams[key]?.split(',') : [];

    if (existingUrlFilterValues.length > 0) {
      selectionsToRemove.forEach((selection: ActivityDestinationViewSearch.SelectionsToRemove) => {
        const indexOfUrlFilterToDelete = existingUrlFilterValues.indexOf(selection.value);
        if (indexOfUrlFilterToDelete > -1) {
          existingUrlFilterValues.splice(indexOfUrlFilterToDelete, 1);
        }
      });
      const updatedUrlFilterValues =
        existingUrlFilterValues.length > 0 ? { [key]: existingUrlFilterValues.join(',') } : {};
      searchQueryString = mergeQueryString(searchQueryString, updatedUrlFilterValues, (queryKey) => {
        if (queryKey === key) {
          return Boolean(updatedUrlFilterValues[queryKey]);
        }

        return Boolean(queryParams[queryKey]);
      });
    }
  });

  return `${location.pathname}?${searchQueryString}`;
}
